<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.FEE.CREATE_OR_UPDATE.SHOW"
    :handler="(data) => openEditor(data)"
  />

  <DialogComponent ref="modal" type="modal" noInnerPadding hideCloseButton>
    <div class="w-full h-full flex flex-col overflow-auto p-4 bg-slate-50">
      <div class="mobile-status-padding"></div>

      <!-- HEADER -->
      <div class="flex justify-between items-center mb-6">
        <button
          class="w-12 h-12 p-3 rounded-full bg-slate-200"
          @click="() => $refs.modal.hide()"
        >
          <ColorImg
            class="w-full h-full bg-primary -rotate-90"
            src="/images/all/arrow-up.svg"
          />
        </button>
        <h2 class="text-2xl font-semibold text-primary">Modifier les frais</h2>
        <div class="w-12"></div>
      </div>

      <!-- INFO -->
      <div class="bg-blue-50 rounded-xl p-4 mb-4">
        <div class="flex items-center gap-2 mb-2">
          <span class="text-2xl">{{ countryInfo?.flag }}</span>
          <div>
            <div class="font-semibold text-gray-900">
              {{ countryInfo?.name }}
            </div>
            <div class="text-sm text-gray-600">{{ operationInfo?.label }}</div>
          </div>
        </div>
      </div>

      <!-- FORM -->
      <div class="flex-1 space-y-4 overflow-auto">
        <!-- PROFIT FEE -->
        <div class="bg-white rounded-xl p-4">
          <div class="flex justify-between items-center mb-2">
            <label class="text-sm font-medium text-gray-700">
              {{ $constants.FEE.FEE_TYPES.profitFee.label }}
            </label>
            <span class="text-xs text-gray-500">%</span>
          </div>
          <input
            v-model.number="form.profitFee"
            type="number"
            min="0"
            max="100"
            step="0.1"
            class="inputComponent w-full"
          />
          <p class="text-xs text-gray-500 mt-1">
            {{ $constants.FEE.FEE_TYPES.profitFee.description }}
          </p>
        </div>

        <!-- PROCESSOR FEE -->
        <div class="bg-white rounded-xl p-4">
          <div class="flex justify-between items-center mb-2">
            <label class="text-sm font-medium text-gray-700">
              {{ $constants.FEE.FEE_TYPES.processorFee.label }}
            </label>
            <span class="text-xs text-gray-500">%</span>
          </div>
          <input
            v-model.number="form.processorFee"
            type="number"
            min="0"
            max="100"
            step="0.1"
            class="inputComponent w-full"
          />
          <p class="text-xs text-gray-500 mt-1">
            {{ $constants.FEE.FEE_TYPES.processorFee.description }}
          </p>
        </div>

        <!-- DISCOUNT FEE -->
        <div class="bg-white rounded-xl p-4">
          <div class="flex justify-between items-center mb-2">
            <label class="text-sm font-medium text-gray-700">
              {{ $constants.FEE.FEE_TYPES.discountFee.label }}
            </label>
            <span class="text-xs text-gray-500">%</span>
          </div>
          <input
            v-model.number="form.discountFee"
            type="number"
            min="0"
            max="100"
            step="0.1"
            class="inputComponent w-full"
          />
          <p class="text-xs text-gray-500 mt-1">
            {{ $constants.FEE.FEE_TYPES.discountFee.description }}
          </p>
        </div>

        <!-- BONUS FEE -->
        <div class="bg-white rounded-xl p-4">
          <div class="flex justify-between items-center mb-2">
            <label class="text-sm font-medium text-gray-700">
              {{ $constants.FEE.FEE_TYPES.bonusFee.label }}
            </label>
            <span class="text-xs text-gray-500">%</span>
          </div>
          <input
            v-model.number="form.bonusFee"
            type="number"
            min="0"
            max="100"
            step="0.1"
            class="inputComponent w-full"
          />
          <p class="text-xs text-gray-500 mt-1">
            {{ $constants.FEE.FEE_TYPES.bonusFee.description }}
          </p>
        </div>

        <!-- AFFILIATION BONUS -->
        <div class="bg-white rounded-xl p-4">
          <div class="flex justify-between items-center mb-2">
            <label class="text-sm font-medium text-gray-700">
              {{ $constants.FEE.FEE_TYPES.affiliationBonus.label }}
            </label>
            <span class="text-xs text-gray-500">%</span>
          </div>
          <input
            v-model.number="form.affiliationBonus"
            type="number"
            min="0"
            max="100"
            step="0.1"
            class="inputComponent w-full"
          />
          <p class="text-xs text-gray-500 mt-1">
            {{ $constants.FEE.FEE_TYPES.affiliationBonus.description }}
          </p>
        </div>

        <!-- MERCHANT SUPPORT FEE -->
        <div class="bg-white rounded-xl p-4">
          <label class="flex items-center justify-between cursor-pointer">
            <div>
              <div class="text-sm font-medium text-gray-700">
                Appliquer les frais sur le montant reçu par le client
              </div>
              <div class="text-xs text-gray-500">
                Les frais sont appliqués sur le montant que le client reçoit
              </div>
            </div>
            <div class="relative">
              <input
                v-model="form.merchantSupportFee"
                type="checkbox"
                class="sr-only"
              />
              <div
                class="w-12 h-6 rounded-full transition-colors"
                :class="
                  form.merchantSupportFee ? 'bg-primary-600' : 'bg-gray-300'
                "
              >
                <div
                  class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
                  :class="
                    form.merchantSupportFee ? 'translate-x-6' : 'translate-x-0'
                  "
                ></div>
              </div>
            </div>
          </label>
        </div>
      </div>

      <!-- TOTAL -->
      <div
        class="bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl p-4 my-4"
      >
        <div class="flex justify-between items-center text-white">
          <span class="font-medium">Total des frais</span>
          <span class="text-2xl font-bold">{{ calculatedServiceFee }}%</span>
        </div>
      </div>

      <!-- ACTIONS -->
      <div class="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-200">
        <button
          class="px-6 py-3 rounded-xl bg-gray-200 text-gray-700 font-medium"
          @click="() => $refs.modal.hide()"
        >
          Annuler
        </button>
        <button
          class="px-6 py-3 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700"
          @click="submit"
        >
          Mettre à jour
        </button>
      </div>
    </div>
  </DialogComponent>
</template>

<script>
export default {
  name: 'FeeEditor',

  data() {
    return {
      form: {
        country: '',
        operation: '',
        processor: 'any',
        serviceFee: 0,
        profitFee: 0,
        processorFee: 0,
        discountFee: 0,
        bonusFee: 0,
        affiliationBonus: 0,
        merchantSupportFee: false,
        service: '{lacasaeBettingServiceName}',
        userId: '{lacasaeBettingServiceName}'
      }
    };
  },

  computed: {
    countryInfo() {
      return this.$constants.FEE.COUNTRIES[this.form.country];
    },
    operationInfo() {
      return this.$constants.FEE.OPERATIONS[this.form.operation];
    },
    calculatedServiceFee() {
      return (
        (this.form.profitFee || 0) + (this.form.processorFee || 0)
      ).toFixed(1);
    },
    totalFees() {
      return (
        (this.form.profitFee || 0) +
        (this.form.processorFee || 0) -
        (this.form.discountFee || 0) +
        (this.form.bonusFee || 0) +
        (this.form.affiliationBonus || 0)
      ).toFixed(1);
    }
  },

  methods: {
    openEditor(fee) {
      if (fee) {
        this.form = {
          country: fee.country,
          operation: fee.operation,
          processor: fee.processor || 'any',
          serviceFee: fee.serviceFee || 0,
          profitFee: fee.profitFee || 0,
          processorFee: fee.processorFee || 0,
          discountFee: fee.discountFee || 0,
          bonusFee: fee.bonusFee || 0,
          affiliationBonus: fee.affiliationBonus || 0,
          merchantSupportFee: fee.merchantSupportFee || false,
          service: fee.service || '{lacasaeBettingServiceName}',
          userId: fee.userId || '{lacasaeBettingServiceName}'
        };
      }
      this.$refs.modal.show();
    },

    submit() {
      const data = {
        ...this.form,
        serviceFee: (this.form.profitFee || 0) + (this.form.processorFee || 0)
      };
      this.$bus.emit(this.$constants.EVENTS.FEE.SUBMIT, data);
      this.$refs.modal.hide();
    }
  }
};
</script>
